import React, { useState, useEffect } from 'react';
import { Modal, List, Button, Space, Typography, Input, Tag, Card, Empty, message, Tooltip, Divider } from 'antd';
import { SearchOutlined, PlusOutlined, CheckOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { SharedStep } from '../../types/sharedStep';
import { sharedStepService } from '../../services/sharedStepService';

interface Props {
    open: boolean;
    onClose: () => void;
    projectId: number;
    folderId: number;
    onAddSteps: (step: SharedStep) => void;
}

export const AddSharedStepsModal: React.FC<Props> = ({
    open,
    onClose,
    projectId,
    folderId,
    onAddSteps,
}) => {
    const [sharedSteps, setSharedSteps] = useState<SharedStep[]>([]);
    const [selectedStep, setSelectedStep] = useState<SharedStep | null>(null);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');

    useEffect(() => {
        if (open) {
            loadSharedSteps();
        }
    }, [open, projectId]);

    const loadSharedSteps = async () => {
        setLoading(true);
        try {
            const steps = await sharedStepService.getSharedSteps(projectId);
            setSharedSteps(steps);
        } catch (error) {
            console.error('Failed to load shared steps:', error);
            message.error('Не удалось загрузить общие шаги');
        } finally {
            setLoading(false);
        }
    };

    const handleAddStep = () => {
        if (selectedStep) {
            onAddSteps(selectedStep);
            setSelectedStep(null);
            onClose();
            message.success('Шаг успешно добавлен');
        }
    };

    const filteredSteps = sharedSteps.filter(step => 
        step.name.toLowerCase().includes(searchText.toLowerCase()) ||
        step.description?.toLowerCase().includes(searchText.toLowerCase())
    );

    return (
        <Modal
            title={
                <Space>
                    <PlusOutlined />
                    <span>Добавить общий шаг</span>
                </Space>
            }
            open={open}
            onCancel={onClose}
            footer={[
                <Button key="cancel" onClick={onClose}>
                    Отмена
                </Button>,
                <Button
                    key="add"
                    type="primary"
                    onClick={handleAddStep}
                    disabled={!selectedStep}
                    icon={<CheckOutlined />}
                >
                    Добавить
                </Button>
            ]}
            width={1000}
            destroyOnClose
        >
            <div style={{ display: 'flex', gap: '16px', height: '500px' }}>
                {/* Левая колонка - список шагов */}
                <div style={{ flex: '0 0 400px', display: 'flex', flexDirection: 'column' }}>
                    <Input
                        placeholder="Поиск по названию или описанию"
                        prefix={<SearchOutlined />}
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        allowClear
                        style={{ marginBottom: '16px' }}
                    />
                    
                    <div style={{ flex: 1, overflow: 'auto', border: '1px solid #f0f0f0', borderRadius: '4px' }}>
                        {filteredSteps.length === 0 ? (
                            <Empty
                                description="Общие шаги не найдены"
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                style={{ marginTop: '100px' }}
                            />
                        ) : (
                            <List
                                loading={loading}
                                dataSource={filteredSteps}
                                renderItem={step => (
                                    <List.Item
                                        style={{
                                            cursor: 'pointer',
                                            backgroundColor: selectedStep?.id === step.id ? '#e6f7ff' : 'transparent',
                                            padding: '12px',
                                            borderBottom: '1px solid #f0f0f0',
                                            transition: 'all 0.3s'
                                        }}
                                        onClick={() => setSelectedStep(step)}
                                    >
                                        <List.Item.Meta
                                            title={
                                                <Space>
                                                    <Typography.Text strong>{step.name}</Typography.Text>
                                                    {selectedStep?.id === step.id && (
                                                        <Tag color="blue">Выбрано</Tag>
                                                    )}
                                                </Space>
                                            }
                                            description={
                                                <Space direction="vertical" style={{ width: '100%' }}>
                                                    {step.description && (
                                                        <Typography.Text type="secondary" ellipsis>
                                                            {step.description}
                                                        </Typography.Text>
                                                    )}
                                                    <Typography.Text type="secondary">
                                                        Количество шагов: {step.steps.length}
                                                    </Typography.Text>
                                                </Space>
                                            }
                                        />
                                    </List.Item>
                                )}
                            />
                        )}
                    </div>
                </div>

                {/* Правая колонка - предпросмотр */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', border: '1px solid #f0f0f0', borderRadius: '4px', padding: '16px' }}>
                    {selectedStep ? (
                        <>
                            <div style={{ marginBottom: '16px' }}>
                                <Space>
                                    <Typography.Title level={5} style={{ margin: 0 }}>
                                        {selectedStep.name}
                                    </Typography.Title>
                                    {selectedStep.description && (
                                        <Tooltip title={selectedStep.description}>
                                            <InfoCircleOutlined style={{ color: '#1890ff' }} />
                                        </Tooltip>
                                    )}
                                </Space>
                                {selectedStep.description && (
                                    <Typography.Paragraph type="secondary" style={{ marginTop: '8px' }}>
                                        {selectedStep.description}
                                    </Typography.Paragraph>
                                )}
                            </div>

                            <Divider style={{ margin: '8px 0' }} />

                            <div style={{ flex: 1, overflow: 'auto' }}>
                                <List
                                    size="small"
                                    dataSource={selectedStep.steps}
                                    renderItem={(s, index) => (
                                        <List.Item style={{ padding: '8px 0' }}>
                                            <Card size="small" style={{ width: '100%' }}>
                                                <Space direction="vertical" style={{ width: '100%' }}>
                                                    <Typography.Text strong>
                                                        Шаг {index + 1}: {s.action}
                                                    </Typography.Text>
                                                    <Typography.Text>
                                                        Ожидаемый результат: {s.result?.expected}
                                                    </Typography.Text>
                                                </Space>
                                            </Card>
                                        </List.Item>
                                    )}
                                />
                            </div>
                        </>
                    ) : (
                        <Empty
                            description="Выберите шаг для предпросмотра"
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            style={{ margin: 'auto' }}
                        />
                    )}
                </div>
            </div>
        </Modal>
    );
}; 